/**
 * scholarshipSchemes.js — SGP Curated Scholarship Scheme Repository
 * 
 * Contains structured, verified government scholarship rules for National Scholarship Portal (NSP)
 * and Tamil Nadu State Portal (UMIS).
 * 
 * Clearly documents sources, review timestamps, and eligibility limits without fabricated rules.
 */

export const SCHOLARSHIP_SCHEMES = [
  {
    id: "pms-sc",
    name: "Post-Matric Scholarship for SC Students (PMS-SC)",
    shortName: "PMS SC",
    provider: "Ministry of Social Justice & Empowerment, Govt. of India",
    portal: "NSP",
    state: "All India / Tamil Nadu",
    category: "SC",
    communities: ["SC"],
    maxIncome: 250000,
    courses: ["ug", "pg", "diploma", "iti", "phd"],
    courseLevels: ["Higher Secondary (11th/12th)", "Undergraduate", "Postgraduate", "Diploma", "ITI", "Ph.D"],
    institutionTypes: ["Government", "Government Aided", "Recognized Private"],
    freshOrRenewal: ["Fresh", "Renewal"],
    amountType: "range",
    amountMin: 4200,
    amountMax: 22300,
    amountNote: "₹4,200 – ₹22,300 / year (maintenance allowance + compulsory non-refundable fees)",
    benefit: "Full tuition fee waiver/reimbursement + monthly maintenance allowance",
    requiredDocuments: [
      "10th / 12th Marksheet",
      "Community / Caste Certificate (SC)",
      "Income Certificate (≤ ₹2,50,000 / annum)",
      "Aadhaar Card (Seeded with Bank)",
      "Bank Passbook (Single Account)",
      "Bonafide / College Fee Receipt",
    ],
    applicationPeriod: "August – November (Annual)",
    officialUrl: "https://scholarships.gov.in",
    sourceUrl: "https://socialjustice.gov.in/schemes/30",
    effectiveFrom: "2020-21",
    lastReviewed: "2025-10-15",
    icon: "🎓",
    color: "#2563eb",
  },
  {
    id: "pms-st",
    name: "Post-Matric Scholarship for ST Students (PMS-ST)",
    shortName: "PMS ST",
    provider: "Ministry of Tribal Affairs, Govt. of India",
    portal: "NSP",
    state: "All India / Tamil Nadu",
    category: "ST",
    communities: ["ST"],
    maxIncome: 250000,
    courses: ["ug", "pg", "diploma", "iti", "phd"],
    courseLevels: ["Higher Secondary", "Undergraduate", "Postgraduate", "Diploma", "ITI", "Ph.D"],
    institutionTypes: ["Government", "Government Aided", "Recognized Private"],
    freshOrRenewal: ["Fresh", "Renewal"],
    amountType: "range",
    amountMin: 4200,
    amountMax: 22300,
    amountNote: "₹4,200 – ₹22,300 / year (maintenance allowance + tuition + exam fees)",
    benefit: "Full non-refundable fees + hostel/day-scholar maintenance allowance",
    requiredDocuments: [
      "10th / 12th Marksheet",
      "Community / Caste Certificate (ST)",
      "Income Certificate (≤ ₹2,50,000 / annum)",
      "Aadhaar Card (Seeded with Bank)",
      "Bank Passbook (Single Account)",
      "Bonafide / College Fee Receipt",
    ],
    applicationPeriod: "August – November (Annual)",
    officialUrl: "https://scholarships.gov.in",
    sourceUrl: "https://tribal.nic.in/Scholarships.aspx",
    effectiveFrom: "2020-21",
    lastReviewed: "2025-10-15",
    icon: "🏕️",
    color: "#059669",
  },
  {
    id: "pms-obc",
    name: "Post-Matric Scholarship for OBC Students (PMS-OBC)",
    shortName: "PMS OBC",
    provider: "Ministry of Social Justice & Empowerment, Govt. of India",
    portal: "NSP",
    state: "All India / Tamil Nadu",
    category: "OBC",
    communities: ["OBC"],
    maxIncome: 100000,
    courses: ["ug", "pg", "diploma", "iti"],
    courseLevels: ["Higher Secondary", "Undergraduate", "Postgraduate", "Diploma", "ITI"],
    institutionTypes: ["Government", "Government Aided", "Recognized Private"],
    freshOrRenewal: ["Fresh", "Renewal"],
    amountType: "range",
    amountMin: 4200,
    amountMax: 12000,
    amountNote: "₹4,200 – ₹12,000 / year (maintenance allowance + fees as per state norms)",
    benefit: "Maintenance allowance and non-refundable fees support",
    requiredDocuments: [
      "10th / 12th Marksheet",
      "OBC Certificate (Competent Authority)",
      "Income Certificate (≤ ₹1,00,000 / annum)",
      "Aadhaar Card (Seeded with Bank)",
      "Bank Passbook (Single Account)",
      "Bonafide Certificate",
    ],
    applicationPeriod: "August – November (Annual)",
    officialUrl: "https://scholarships.gov.in",
    sourceUrl: "https://socialjustice.gov.in",
    effectiveFrom: "2021-22",
    lastReviewed: "2025-10-15",
    icon: "📘",
    color: "#7c3aed",
  },
  {
    id: "csss",
    name: "PM-USP — Central Sector Scheme of Scholarships (CSSS)",
    shortName: "CSSS (PM-USP)",
    provider: "Department of Higher Education, Ministry of Education, Govt. of India",
    portal: "NSP",
    state: "All India",
    category: "All",
    communities: ["SC", "ST", "OBC", "EBC", "BC", "MBC", "GEN"],
    maxIncome: 450000,
    courses: ["ug", "pg"],
    courseLevels: ["Undergraduate (Degree)", "Postgraduate (Degree)"],
    institutionTypes: ["Colleges / Universities recognized by AICTE / UGC / MCI"],
    freshOrRenewal: ["Fresh", "Renewal"],
    amountType: "tiered",
    amountUG: 12000,
    amountPG: 20000,
    amountNote: "₹12,000 / year for UG (1st–3rd year) | ₹20,000 / year for PG (4th–5th year)",
    benefit: "Direct bank transfer for academic maintenance and expenses",
    requiredDocuments: [
      "12th Marksheet (Top 80th percentile in respective State/CBSE board)",
      "Income Certificate (≤ ₹4,50,000 / annum)",
      "Aadhaar Card (Seeded with Bank)",
      "Bank Passbook (Single Account)",
      "College Admission / Bonafide Proof",
    ],
    applicationPeriod: "September – December (Annual)",
    officialUrl: "https://scholarships.gov.in",
    sourceUrl: "https://www.education.gov.in/scholarships-education-loan-0",
    effectiveFrom: "2022-23",
    lastReviewed: "2025-10-15",
    icon: "🏛️",
    color: "#0891b2",
  },
  {
    id: "yasasvi",
    name: "PM-YASASVI — OBC / EBC / DNT Post-Matric",
    shortName: "PM-YASASVI",
    provider: "Ministry of Social Justice & Empowerment, Govt. of India",
    portal: "NSP",
    state: "All India",
    category: "OBC / EBC / DNT",
    communities: ["OBC", "EBC", "DNC", "BC", "MBC"],
    maxIncome: 250000,
    courses: ["ug", "pg", "diploma"],
    courseLevels: ["Higher Secondary (11th/12th)", "Undergraduate", "Postgraduate", "Diploma"],
    institutionTypes: ["Top-class schools/colleges notified by Ministry"],
    freshOrRenewal: ["Fresh", "Renewal"],
    amountType: "full",
    amountMin: 75000,
    amountMax: 200000,
    amountNote: "Full financial support (tuition fee + living expenses in top institutions)",
    benefit: "Complete educational support for OBC, EBC and Nomadic / Semi-Nomadic communities",
    requiredDocuments: [
      "10th / 12th Marksheet",
      "OBC / EBC / DNT Category Certificate",
      "Income Certificate (≤ ₹2,50,000 / annum)",
      "Aadhaar Card",
      "Bank Passbook (Single Account)",
      "Institutional Admission Proof",
    ],
    applicationPeriod: "August – November (Annual)",
    officialUrl: "https://scholarships.gov.in",
    sourceUrl: "https://socialjustice.gov.in",
    effectiveFrom: "2022-23",
    lastReviewed: "2025-10-15",
    icon: "🏅",
    color: "#d97706",
  },
  {
    id: "minority-pms",
    name: "Post-Matric Scholarship for Minority Communities",
    shortName: "Minority Post-Matric",
    provider: "Ministry of Minority Affairs, Govt. of India",
    portal: "NSP",
    state: "All India",
    category: "Minority",
    communities: ["MIN"],
    maxIncome: 200000,
    courses: ["ug", "pg", "diploma", "iti", "phd"],
    courseLevels: ["Class 11 to Ph.D"],
    institutionTypes: ["Government / Private recognized institutions"],
    freshOrRenewal: ["Fresh", "Renewal"],
    amountType: "range",
    amountMin: 3000,
    amountMax: 12000,
    amountNote: "₹3,000 – ₹12,000 / year (admission + tuition fee + maintenance allowance)",
    benefit: "Financial assistance for Muslims, Christians, Sikhs, Buddhists, Parsis, and Jains",
    requiredDocuments: [
      "Previous Qualifying Exam Marksheet (Minimum 50% marks)",
      "Self-Declaration of Minority Community",
      "Income Certificate (≤ ₹2,00,000 / annum)",
      "Aadhaar Card",
      "Bank Passbook (Single Account)",
      "Fee Receipt / Bonafide",
    ],
    applicationPeriod: "September – November (Annual)",
    officialUrl: "https://scholarships.gov.in",
    sourceUrl: "https://www.minorityaffairs.gov.in",
    effectiveFrom: "2020-21",
    lastReviewed: "2025-10-15",
    icon: "🌙",
    color: "#db2777",
  },
  {
    id: "pmss",
    name: "Prime Minister's Scholarship Scheme for Wards of Armed Forces (PMSS)",
    shortName: "PMSS (Armed Forces)",
    provider: "Kendriya Sainik Board / Ministry of Home Affairs & Defence",
    portal: "NSP / KSB",
    state: "All India",
    category: "Armed Forces Wards",
    communities: ["SC", "ST", "OBC", "EBC", "BC", "MBC", "GEN", "MIN", "DNC"],
    maxIncome: 999999999, // No income ceiling for recognized wards
    courses: ["ug", "pg", "diploma"],
    courseLevels: ["Professional Degree Courses (BE, B.Tech, MBBS, BDS, MBA, MCA etc.)"],
    institutionTypes: ["AICTE / UGC / MCI / DCI approved professional colleges"],
    freshOrRenewal: ["Fresh", "Renewal"],
    specialCondition: "armed_forces",
    amountType: "fixed",
    amountBoys: 24000,
    amountGirls: 27000,
    amountNote: "₹2,000 / month (Boys — ₹24,000/yr) | ₹2,250 / month (Girls — ₹27,000/yr)",
    benefit: "Monthly scholarship for higher technical and professional education",
    requiredDocuments: [
      "12th / Diploma / Degree Marksheet (Minimum 60% marks)",
      "Ex-Servicemen Certificate / ESM Card (Annexure-1)",
      "Bonafide Certificate from College",
      "Aadhaar Card",
      "Bank Passbook (Single Account)",
    ],
    applicationPeriod: "September – December (Annual)",
    officialUrl: "https://scholarships.gov.in",
    sourceUrl: "https://www.ksb.gov.in",
    effectiveFrom: "2021-22",
    lastReviewed: "2025-10-15",
    icon: "🪖",
    color: "#65a30d",
  },
  {
    id: "tn-bc-mbc",
    name: "Tamil Nadu State Post-Matric Scholarship (BC / MBC / DNC)",
    shortName: "TN BC/MBC Post-Matric",
    provider: "BC, MBC & Minorities Welfare Department, Govt. of Tamil Nadu",
    portal: "UMIS",
    state: "Tamil Nadu",
    category: "BC / MBC / DNC",
    communities: ["BC", "MBC", "DNC"],
    maxIncome: 200000,
    courses: ["ug", "pg", "diploma", "iti"],
    courseLevels: ["Arts, Science, Polytechnic, Engineering, Medical, Diploma, ITI"],
    institutionTypes: ["Government, Aided & Self-Financing Institutions in Tamil Nadu"],
    freshOrRenewal: ["Fresh", "Renewal"],
    amountType: "range",
    amountMin: 6000,
    amountMax: 15000,
    amountNote: "₹6,000 – ₹15,000 / year (tuition fees reimbursement + special fees + maintenance)",
    benefit: "Fee reimbursement and monthly allowance for Tamil Nadu state students",
    requiredDocuments: [
      "10th & 12th Marksheets",
      "Tamil Nadu Community Certificate (BC / MBC / DNC)",
      "Income Certificate (≤ ₹2,00,000 / annum)",
      "Aadhaar Card (Seeded with Bank)",
      "Bank Passbook (Single Account)",
      "College Bonafide & UMIS Registration Number",
    ],
    applicationPeriod: "August – October (Annual)",
    officialUrl: "https://umis.tn.gov.in",
    sourceUrl: "https://www.bcmbcmw.tn.gov.in",
    effectiveFrom: "2021-22",
    lastReviewed: "2025-10-15",
    icon: "🗺️",
    color: "#9333ea",
  },
];

/**
 * Filter potentially matching scholarship schemes based on student input.
 */
export function matchScholarshipSchemes({
  income,
  community,
  course,
  armedForces = "no",
}) {
  const inc = parseInt(income, 10);
  const comm = (community || "").toUpperCase().trim();
  const crs = (course || "").toLowerCase().trim();
  const isAF = armedForces === "yes";

  return SCHOLARSHIP_SCHEMES.filter((s) => {
    if (s.specialCondition === "armed_forces" && !isAF) return false;
    const commMatch = s.communities.includes(comm) || (s.communities.includes("MIN") && ["MUSLIM", "CHRISTIAN", "SIKH", "BUDDHIST", "JAIN", "PARSI"].includes(comm));
    const incomeMatch = !isNaN(inc) && inc <= s.maxIncome;
    const courseMatch = !crs || s.courses.includes(crs);

    return commMatch && incomeMatch && courseMatch;
  });
}
